import { AnalyticsProvider } from '@inboundx/shared/analytics';
import { FetchNetworkAdapter } from '@inboundx/shared/platform';
import { DisplayController } from '@inboundx/shared/widget';
import { Bot, Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { ConfigurableStorageAdapter } from '../adapters/ConfigurableStorageAdapter';
import { ClientConfigProvider } from '../components/ClientConfigProvider';
import { getConfig } from '../config';
import { API_ENDPOINTS } from '../constants/endpoints';
import { useAppContext } from '../contexts/AppContext';

export default function FormAssistantTestPage() {
    const { selectedEndpoint, customEndpoint, useCustomEndpoint, selectedClient } = useAppContext();

    const [networkAdapter] = useState(new FetchNetworkAdapter());

    // Get current API URL based on endpoint selection
    const getCurrentApiUrl = () => {
        if (useCustomEndpoint && customEndpoint) {
            return customEndpoint;
        }
        return selectedEndpoint?.url || API_ENDPOINTS[0].url;
    };

    // Create storage adapter that updates when endpoint changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: getCurrentApiUrl closes over the listed deps — listing them instead of the unstable function reference
    const storageAdapter = useMemo(
        () => new ConfigurableStorageAdapter(getCurrentApiUrl),
        [selectedEndpoint, customEndpoint, useCustomEndpoint]
    );

    const currentApiUrl = getCurrentApiUrl();

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <Bot className="h-8 w-8 text-purple-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Form Assistant Test</h1>
                    <p className="text-lg text-gray-600">
                        Testing the form assistant feature (widget in copilot form factor)
                    </p>
                </div>

                {/* Info Card */}
                <Card className="p-6 bg-purple-50 border-purple-200 mb-8">
                    <div className="flex items-start">
                        <Info className="h-5 w-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="text-sm text-purple-800">
                            <p className="font-semibold mb-2">Form Assistant Behavior:</p>
                            <ul className="space-y-1 list-disc list-inside">
                                <li>Widget appears as "Need help?" button at bottom-right</li>
                                <li>Clicking expands to a right-side panel layout</li>
                                <li>
                                    First expansion shows a welcome message with form-related
                                    suggested questions
                                </li>
                                <li>Different placeholder text: "How can we help you?"</li>
                            </ul>
                            <p className="mt-3 text-purple-600">
                                <strong>Note:</strong> This page forces{' '}
                                <code className="bg-purple-100 px-1 rounded">
                                    formFactor="copilot"
                                </code>{' '}
                                and{' '}
                                <code className="bg-purple-100 px-1 rounded">
                                    isFormAssistant=true
                                </code>{' '}
                                on the widget.
                            </p>
                        </div>
                    </div>
                </Card>

                {/* Content placeholder sections */}
                <div className="space-y-6">
                    <Card className="p-6 bg-white/80">
                        <h2 className="text-xl font-semibold text-gray-800 mb-3">
                            Sample Form Page Content
                        </h2>
                        <p className="text-gray-600 mb-4">
                            This simulates a CTA destination page where the form assistant would
                            normally appear. The widget is positioned at the bottom-right corner in
                            copilot mode.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            </div>
                        </div>
                    </Card>

                    {[1, 2, 3].map((i) => (
                        <Card key={i} className="p-6 bg-white/60">
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                Section {i}
                            </h3>
                            <p className="text-gray-600">
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                                eiusmod tempor incididunt ut labore et dolore magna aliqua.
                            </p>
                        </Card>
                    ))}
                </div>

                {/* Client info */}
                {selectedClient && (
                    <Card className="mt-6 p-4 bg-gray-50 border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                            Current Configuration
                        </h3>
                        <div className="space-y-1 text-xs text-gray-600">
                            <div>
                                <strong>Client:</strong> {selectedClient.name} (
                                {selectedClient.domain})
                            </div>
                            <div>
                                <strong>API URL:</strong> <code>{currentApiUrl}</code>
                            </div>
                            <div>
                                <strong>Form Factor:</strong> <code>copilot</code> (forced)
                            </div>
                            <div>
                                <strong>Form Assistant:</strong> <code>true</code> (forced)
                            </div>
                        </div>
                    </Card>
                )}
            </div>

            {/* Widget with forced copilot mode and form assistant */}
            {selectedClient && (
                <ClientConfigProvider
                    client={selectedClient}
                    apiUrl={currentApiUrl}
                    apiKey={getConfig().apiKey}
                >
                    <AnalyticsProvider>
                        <DisplayController
                            apiUrl={currentApiUrl}
                            apiKey={getConfig().apiKey}
                            siteName={selectedClient.siteName}
                            domain={selectedClient.domain}
                            storageAdapter={storageAdapter}
                            networkAdapter={networkAdapter}
                            forceDisplay={true}
                            formFactor="copilot"
                            isFormAssistant={true}
                            visitorIp={getConfig().visitorIp}
                        />
                    </AnalyticsProvider>
                </ClientConfigProvider>
            )}
        </div>
    );
}
